import type { FormEvent } from 'react';
import React, { Suspense, useEffect, useState } from 'react';
import {
    CreativeTypes,
    ElementUpdateTypes,
    type ImageElement,
    type MultiPageVideoModel,
} from '@bynder-studio/render-core';
import { Divider, Form, List, Thumbnail } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconAdjust, IconArrowDown, IconArrowUp } from '@bynder/icons';
import useAccessRights from 'packages/hooks/useAccessRights';
import useEditor from 'packages/hooks/useEditor';
import useEditorSelectedElement from 'packages/hooks/useEditorSelectedElement';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import features from 'packages/configs/features';
import AuthorizationHelper from 'packages/helpers/AuthorizationHelper';
import { FormSectionContent, InspectorContent, InspectorHeader, Section } from '../../index.styled';
import SettingsTabs from '../Tabs';
import LockToggler from '../Size/LockToggle';
import { SizeFormRow } from './index.styled';
import ElementAlignment from '../ElementAlignment';
import VerticalPosition from '../Position/Vertical';
import HorizontalPosition from '../Position/Horizontal';
import Rotation from '../Rotation';
import Opacity from '../Opacity';
import Height from '../Size/Height';
import Width from '../Size/Width';
import Duration from '../Duration';
import Shadow from '../Shadow';
import CropMode from '../CropMode';
import Masking from '../Masking';
import { BlendMode } from '../BlendMode';
import BrowseImage from './BrowseImage';
import Animations from '../../Animations';
import AssetSource from '../AssetSource';
import PersonalUploads from '../PersonalUploads';
import AllowToggleVisibility from '../AllowToggleVisibility';
import ContentProperty from '../ContentProperty';
import { useEffectivelyLocked } from 'packages/hooks/useEffectivelyLocked';
import useEditorSidebarShortcuts from 'packages/hooks/shortcuts/useEditorSidebarShortcuts';

const Image = () => {
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const { creativeModel, creativeType, manipulationRenderer, frameRate } = useEditor();
    const { selectedElement, selectedElements } = useEditorSelectedElement<ImageElement>();
    const isLockedByParent = useEffectivelyLocked();

    const tabs = [
        { value: 'image', label: translate('editor.sidebar.shots.image.tabs.text') },
        { value: 'image_animation', label: translate('editor.sidebar.shots.image.tabs.animation') },
    ];

    const [activeTab, setTab] = useState(tabs[0].value);
    const [imageSettingsExpanded, setImageSettingsExpanded] = useState(false);

    const isInternalAssetsAvailable = AuthorizationHelper.isFeatureAvailable(features.INTERNAL_ASSET);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        const unsubscribeLock = creativeModel.onElementsPartialUpdate(ElementUpdateTypes.LOCK, forceUpdate);

        return () => {
            unsubscribeLock();
        };
    }, [creativeModel, forceUpdate]);

    const defaultProps = {
        creativeModel,
        selectedElement,
        disabled: isLockedByParent,
    };

    useEditorSidebarShortcuts({
        setAnimationsTab: () => {
            if (creativeType === 'VIDEO') {
                setTab(tabs[1].value);
            }
        },
    });

    return (
        <>
            {creativeType === 'VIDEO' && (
                <InspectorHeader aria-labelledby="shots-text-tabs-settings">
                    <SettingsTabs tabs={tabs} activeTab={activeTab} onChange={(value) => setTab(value)} />
                </InspectorHeader>
            )}
            <InspectorContent withTabs={creativeType === 'VIDEO'}>
                {activeTab === tabs[0].value && (
                    <>
                        <Section aria-labelledby="shots-alignment-settings">
                            <ElementAlignment creativeModel={creativeModel} selectedElements={selectedElements} />
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-size-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <SizeFormRow>
                                    <Width {...defaultProps} creativeType={creativeType} />
                                    <LockToggler selectedElement={selectedElement} forceUpdate={forceUpdate} />
                                    <Height {...defaultProps} creativeType={creativeType} />
                                </SizeFormRow>
                                <Form.Row>
                                    <HorizontalPosition {...defaultProps} />
                                    <VerticalPosition {...defaultProps} />
                                </Form.Row>
                                <Form.Row>
                                    <Rotation {...defaultProps} />
                                    <Opacity {...defaultProps} />
                                </Form.Row>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        {isPlatformAllowed([features.CONTENT_PROPERTIES]) && (
                            <>
                                <Section aria-labelledby="shots-text-content-property">
                                    <Suspense>
                                        <ContentProperty
                                            creativeModel={creativeModel}
                                            selectedElements={selectedElements}
                                            disabled={defaultProps.disabled}
                                        />
                                    </Suspense>
                                </Section>
                                <Divider />
                            </>
                        )}
                        <Section aria-labelledby="shots-image-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <BrowseImage {...defaultProps} />
                                <List.Item
                                    thumbnail={<Thumbnail icon={<IconAdjust />} />}
                                    rightElements={imageSettingsExpanded ? <IconArrowUp /> : <IconArrowDown />}
                                    onClick={() => setImageSettingsExpanded((state) => !state)}
                                >
                                    {translate('design.sidebar.scene.options.expand.label', {
                                        type: translate('editor.sidebar.shots.image.tabs.text'),
                                    })}
                                </List.Item>
                                <div style={imageSettingsExpanded ? undefined : { display: 'none' }}>
                                    <CropMode disabled={defaultProps.disabled} />
                                </div>
                                {imageSettingsExpanded && creativeType === CreativeTypes.VIDEO && (
                                    <>
                                        <Divider />
                                        <Duration
                                            {...defaultProps}
                                            creativeModel={creativeModel as MultiPageVideoModel}
                                            frameRate={frameRate}
                                        />
                                    </>
                                )}
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-image-asset-source">
                            <Form onSubmit={preventSubmit}>
                                <Form.Group>
                                    <AssetSource disabled={defaultProps.disabled} />
                                </Form.Group>
                                {isInternalAssetsAvailable && (
                                    <>
                                        <Divider />
                                        <Form.Group>
                                            <PersonalUploads
                                                selectedElements={[selectedElement]}
                                                disabled={defaultProps.disabled}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </Form>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-image-visibility">
                            <Form onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.visibility_control')}</Form.Label>
                                    <AllowToggleVisibility
                                        creativeModel={creativeModel}
                                        selectedElements={[selectedElement]}
                                        disabled={defaultProps.disabled}
                                    />
                                </Form.Group>
                            </Form>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-image-masking-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.mask')}</Form.Label>
                                    <Masking
                                        {...defaultProps}
                                        manipulationRenderer={manipulationRenderer}
                                        creativeType={creativeType}
                                    />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-image-shadow">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.sidebar.shots.image.shadow.label')}</Form.Label>
                                    <Shadow {...defaultProps} />
                                </Form.Group>
                                <Divider />
                                <Form.Group>
                                    <BlendMode disabled={defaultProps.disabled} />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                    </>
                )}
                {activeTab === tabs[1].value && <Animations element={selectedElement} disabled={isLockedByParent} />}
            </InspectorContent>
        </>
    );
};

export default Image;

function preventSubmit(e: FormEvent) {
    e.preventDefault();
}
